import { Box, Group, Stack, Text } from "@mantine/core";
import { Palette } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";

interface TokenGroup {
  label: string;
  vars: string[];
  showSwatch?: boolean;
}

const GROUPS: TokenGroup[] = [
  {
    label: "Brand",
    showSwatch: true,
    vars: [
      "--mantine-color-brand-0",
      "--mantine-color-brand-1",
      "--mantine-color-brand-3",
      "--mantine-color-brand-5",
      "--mantine-color-brand-6",
      "--mantine-color-brand-7",
      "--mantine-color-brand-9",
    ],
  },
  {
    label: "Neutral",
    showSwatch: true,
    vars: [
      "--mantine-color-white",
      "--mantine-color-gray-1",
      "--mantine-color-gray-2",
      "--mantine-color-gray-6",
      "--mantine-color-gray-9",
      "--mantine-color-dark-9",
    ],
  },
  {
    label: "Spacing",
    vars: [
      "--mantine-spacing-xs",
      "--mantine-spacing-sm",
      "--mantine-spacing-md",
      "--mantine-spacing-lg",
      "--mantine-spacing-xl",
    ],
  },
  {
    label: "Radius",
    vars: [
      "--mantine-radius-sm",
      "--mantine-radius-md",
      "--mantine-radius-lg",
      "--mantine-radius-xl",
    ],
  },
  {
    label: "Font sizes",
    vars: [
      "--mantine-font-size-xs",
      "--mantine-font-size-sm",
      "--mantine-font-size-md",
      "--mantine-font-size-lg",
    ],
  },
];

function readVars(target: Element, names: string[]): Record<string, string> {
  const out: Record<string, string> = {};
  if (typeof window === "undefined") return out;
  const cs = window.getComputedStyle(target);
  for (const name of names) out[name] = cs.getPropertyValue(name).trim();
  return out;
}

interface Props {
  /** Bumped whenever the live theme changes so we re-read computed vars. */
  refreshKey?: unknown;
  /**
   * Where to sample the CSS variables from.
   * - `"root"` (default): reads from `:root` — the committed tenant theme that's
   *   actually live across the app right now (ignores any local MantineProvider
   *   overrides such as a tenant-edit preview).
   * - `"local"`: reads from the panel's own DOM node — picks up the nearest
   *   MantineProvider override (useful when you want to see preview values).
   */
  source?: "root" | "local";
}

export function CssTokensPanel({ refreshKey, source = "root" }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const target = source === "local" ? ref.current : document.documentElement;
      if (!target) return;
      const all = GROUPS.flatMap((g) => g.vars);
      setValues(readVars(target, all));
    });
    return () => cancelAnimationFrame(frame);
  }, [refreshKey, source]);

  return (
    <Stack gap="md" ref={ref}>
      {GROUPS.map((group) => (
        <Box key={group.label}>
          <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: "0.06em" }} mb={6}>
            {group.label}
          </Text>
          <Stack gap={4}>
            {group.vars.map((name) => {
              const value = values[name] || "";
              return (
                <Group key={name} justify="space-between" gap={6} wrap="nowrap">
                  <Group gap={6} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                    {group.showSwatch && (
                      <Box
                        style={{
                          width: 14,
                          height: 14,
                          flexShrink: 0,
                          borderRadius: 4,
                          background: value || "transparent",
                          border: "1px solid var(--mantine-color-gray-2)",
                        }}
                      />
                    )}
                    <Text
                      size="xs"
                      style={{
                        fontFamily: "ui-monospace, monospace",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Text>
                  </Group>
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{
                      fontFamily: "ui-monospace, monospace",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    {value || "—"}
                  </Text>
                </Group>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export const CssTokensPanelHeader = () => (
  <Group gap="sm" align="center">
    <Box
      style={{
        width: 28,
        height: 28,
        borderRadius: 8,
        background: "color-mix(in oklch, var(--mantine-color-brand-6) 14%, transparent)",
        color: "var(--mantine-color-brand-6)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Palette size={16} />
    </Box>
  </Group>
);
